/**
 * Zone File Parser and Generator
 * Handles BIND-style zone file format for DNS records import/export
 */

import { normalizeNameForApi, formatNameForDisplay } from './dnsName'

export interface ZoneRecord {
  name: string
  type: string
  content: string
  ttl?: number
  priority?: number
}

export interface ParseResult {
  records: ZoneRecord[]
  errors: string[]
  defaultTtl: number
}

// Supported record types
const SUPPORTED_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA']

/**
 * Parse a BIND-style zone file into DNS records
 */
export function parseZoneFile(content: string): ParseResult {
  const lines = content.split('\n')
  const records: ZoneRecord[] = []
  const errors: string[] = []
  let defaultTtl = 3600

  for (let i = 0; i < lines.length; i++) {
    const lineNum = i + 1
    const rawLine = lines[i]
    if (rawLine === undefined) continue
    const line = rawLine.trim()

    // Skip empty lines and comments
    if (!line || line.startsWith(';')) continue

    // Handle $TTL directive
    if (line.startsWith('$TTL')) {
      const match = line.match(/^\$TTL\s+(\d+)/i)
      if (match?.[1]) {
        defaultTtl = parseInt(match[1], 10)
      }
      continue
    }

    // Skip other directives ($ORIGIN, etc.)
    if (line.startsWith('$')) continue

    // Parse record line
    try {
      const record = parseRecordLine(line, defaultTtl)
      if (record) {
        records.push(record)
      }
    } catch (e) {
      errors.push(`Zeile ${lineNum}: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`)
    }
  }

  return { records, errors, defaultTtl }
}

/**
 * Parse a single record line
 * Format: name [ttl] [class] type content
 */
function parseRecordLine(line: string, defaultTtl: number): ZoneRecord | null {
  // Remove inline comments
  const commentIndex = line.indexOf(';')
  if (commentIndex > 0) {
    line = line.substring(0, commentIndex).trim()
  }

  // Handle quoted strings (for TXT records)
  const parts: string[] = []
  let current = ''
  let inQuote = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuote = !inQuote
      current += char
    } else if ((char === ' ' || char === '\t') && !inQuote) {
      if (current) {
        parts.push(current)
        current = ''
      }
    } else {
      current += char
    }
  }
  if (current) parts.push(current)

  if (parts.length < 3) return null

  let idx = 0
  const name = parts[idx++]
  if (!name) return null
  let ttl: number | undefined
  let content: string
  let priority: number | undefined

  // Check if next part is TTL (number)
  const nextPart = parts[idx]
  if (nextPart && /^\d+$/.test(nextPart)) {
    ttl = parseInt(parts[idx++]!, 10)
  }

  // Skip class (IN)
  if (parts[idx]?.toUpperCase() === 'IN') {
    idx++
  }

  // Record type
  const recordType = parts[idx++]?.toUpperCase()

  if (!recordType || !SUPPORTED_TYPES.includes(recordType)) {
    if (recordType) {
      throw new Error(`Nicht unterstuetzter Typ: ${recordType}`)
    }
    return null
  }

  // Handle MX priority
  if (recordType === 'MX') {
    const mxPart = parts[idx]
    if (mxPart && /^\d+$/.test(mxPart)) {
      priority = parseInt(parts[idx++]!, 10)
    }
  }

  // Handle SRV (priority weight port target)
  if (recordType === 'SRV') {
    if (parts.length >= idx + 4) {
      const srvPart = parts[idx]
      if (srvPart) {
        priority = parseInt(srvPart, 10)
        idx++
      }
      // Skip weight and port, include in content
      content = parts.slice(idx).join(' ')
    } else {
      content = parts.slice(idx).join(' ')
    }
  } else {
    // Rest is content
    content = parts.slice(idx).join(' ')
  }

  // Clean up content
  content = content.trim()

  // Remove surrounding quotes from TXT records
  if (recordType === 'TXT' && content.startsWith('"') && content.endsWith('"')) {
    content = content.slice(1, -1)
  }

  // Remove trailing dot from FQDNs
  if (content.endsWith('.')) {
    content = content.slice(0, -1)
  }

  return {
    name: normalizeNameForApi(name),
    type: recordType,
    content,
    ttl: ttl ?? defaultTtl,
    priority
  }
}

/**
 * Generate a BIND-style zone file from DNS records
 */
export function generateZoneFile(
  records: ZoneRecord[],
  domain: string,
  defaultTtl: number = 3600
): string {
  const lines: string[] = []

  // Header
  lines.push(`; Zone file for ${domain}`)
  lines.push(`; Generated: ${new Date().toISOString()}`)
  lines.push(`; Records: ${records.length}`)
  lines.push('')
  lines.push(`$TTL ${defaultTtl}`)
  lines.push('')

  // Group records by type for better readability
  const grouped = new Map<string, ZoneRecord[]>()
  for (const record of records) {
    const type = record.type
    if (!grouped.has(type)) {
      grouped.set(type, [])
    }
    grouped.get(type)!.push(record)
  }

  // Output in order: A, AAAA, CNAME, MX, TXT, NS, SRV, CAA
  const typeOrder = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA']

  for (const type of typeOrder) {
    const typeRecords = grouped.get(type)
    if (!typeRecords || typeRecords.length === 0) continue

    lines.push(`; ${type} Records`)
    for (const record of typeRecords) {
      lines.push(formatRecordLine(record, defaultTtl))
    }
    lines.push('')
  }

  // Any remaining types not in the order
  for (const [type, typeRecords] of grouped) {
    if (typeOrder.includes(type)) continue
    lines.push(`; ${type} Records`)
    for (const record of typeRecords) {
      lines.push(formatRecordLine(record, defaultTtl))
    }
    lines.push('')
  }

  return lines.join('\n')
}

/**
 * Format a single record as a zone file line
 */
function formatRecordLine(record: ZoneRecord, defaultTtl: number): string {
  const name = formatNameForDisplay(record.name)
  const ttl = record.ttl !== defaultTtl ? record.ttl : ''
  const type = record.type

  let content = record.content

  // Add trailing dot to FQDNs for CNAME, MX, NS
  if (['CNAME', 'MX', 'NS'].includes(type) && content && !content.endsWith('.') && content !== '@') {
    content = content + '.'
  }

  // Quote TXT content
  if (type === 'TXT') {
    content = `"${content}"`
  }

  // Build line parts
  const parts = [name.padEnd(20)]
  if (ttl) parts.push(String(ttl).padStart(6))
  parts.push('IN')
  parts.push(type.padEnd(6))

  // Add priority for MX
  if (type === 'MX' && record.priority !== undefined) {
    parts.push(String(record.priority).padStart(3))
  }

  parts.push(content)

  return parts.join('\t')
}

/**
 * Download zone file content as a file
 */
export function downloadZoneFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Read file content as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'))
    reader.readAsText(file)
  })
}
